import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type {
  ClientDetail,
  ClientNoteInput,
  ClientNoteView,
  ClientPortalLoginInput,
  ClientPreferenceInput,
  ClientPreferenceView,
  ClientPropertyInput,
  ClientPropertyView,
  ClientView,
  CreateClientInput,
  CreatedClientPortalLogin,
  UpdateClientInput,
} from '@shared/schemas/client';
import type { FreshWindow, ImportPreview, ImportResult } from '@shared/schemas/client-import';
import type { ActivityFilter, ListResponse, PeopleSort } from '@shared/schemas/pagination';

/* The queries behind /admin/clients. ROADMAP.md §3.5 and §3.6. */

const ROOT = ['admin', 'clients'] as const;

export interface ClientListParams {
  sort: PeopleSort;
  page: number;
  search: string;
  activity: ActivityFilter;
  highPriorityOnly: boolean;
  /** Defaults to the server's 25. The picker in the job dialog asks for more. */
  pageSize?: number;
}

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

export function useClients(params: ClientListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<ClientView>>(
        `/api/admin/clients${queryString({
          page: params.page,
          sort: params.sort,
          search: params.search,
          activity: params.activity,
          pageSize: params.pageSize,
          ...(params.highPriorityOnly ? { highPriorityOnly: 'true' } : {}),
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, 'detail', id],
    queryFn: () => api.get<ClientDetail>(`/api/admin/clients/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

export function useCreateClient() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) => api.post<ClientDetail>('/api/admin/clients', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateClient(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClientInput) =>
      api.patch<ClientDetail>(`/api/admin/clients/${id}`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useSetClientActive(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<ClientDetail>(`/api/admin/clients/${id}/${isActive ? 'reactivate' : 'archive'}`),
    onSuccess: () => invalidate(client),
  });
}

export function useSaveProperty(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      input,
    }: {
      propertyId: string | null;
      input: ClientPropertyInput;
    }) =>
      propertyId === null
        ? api.post<ClientPropertyView>(`/api/admin/clients/${clientId}/properties`, input)
        : api.patch<ClientPropertyView>(
            `/api/admin/clients/${clientId}/properties/${propertyId}`,
            input,
          ),
    onSuccess: () => invalidate(client),
  });
}

export function usePropertyAction(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      action,
    }: {
      propertyId: string;
      action: 'primary' | 'retire' | 'reactivate';
    }) => api.post(`/api/admin/clients/${clientId}/properties/${propertyId}/${action}`),
    onSuccess: () => invalidate(client),
  });
}

/**
 * Read a customer list, and either say what it would do or do it.
 *
 * **One endpoint for both**, with `commit` deciding — the preview and the
 * import are the same parse on the server, so the screen cannot promise
 * something the writes then do differently.
 *
 * `FormData` rather than JSON, because it carries a file.
 */
export function useClientImport() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      freshDays,
      commit,
    }: {
      file: File;
      freshDays: FreshWindow;
      commit: boolean;
    }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('freshDays', String(freshDays));
      // A string, because that is what multipart carries. The server coerces.
      form.append('commit', commit ? 'true' : 'false');

      return api.upload<{ preview: ImportPreview; result: ImportResult | null }>(
        '/api/admin/clients/import',
        form,
      );
    },
    // Only a commit changes anything, but invalidating on a preview costs one
    // refetch and removes a way for the list to go stale.
    onSuccess: () => invalidate(client),
  });
}

export function useAddNote(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientNoteInput) =>
      api.post<ClientNoteView>(`/api/admin/clients/${clientId}/notes`, input),
    onSuccess: () => invalidate(client),
  });
}

/**
 * Pin a note to the top, or unpin it.
 *
 * **The only thing about a note that can change**, and it moves the note rather
 * than editing it — see `clientService.setNotePinned`.
 */
export function useSetNotePinned(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, isPinned }: { noteId: string; isPinned: boolean }) =>
      api.patch<ClientNoteView>(`/api/admin/clients/${clientId}/notes/${noteId}`, { isPinned }),
    onSuccess: () => invalidate(client),
  });
}

export function useSavePreferences(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientPreferenceInput) =>
      api.put<ClientPreferenceView>(`/api/admin/clients/${clientId}/preferences`, input),
    onSuccess: () => invalidate(client),
  });
}

/**
 * Open the client portal to this customer — ROADMAP.md §5.1.
 *
 * The response carries a password that exists nowhere else, so the caller must
 * put it on the screen and say so. Deliberately not cached: this is a one-shot
 * result, and a query cache holding somebody's password is a query cache
 * holding somebody's password.
 */
export function useCreatePortalLogin(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientPortalLoginInput) =>
      api.post<CreatedClientPortalLogin>(`/api/admin/clients/${clientId}/portal-login`, input),
    onSuccess: () => invalidate(client),
  });
}
